/*
	Navigation carousel plugin - syncs with another carousel and provides nav-specific functionality:
	- Always centers the selected slide
	- Provides arrow key navigation
	- Adds navigation-specific styling classes
	- Syncs bidirectionally with target carousel
*/
export default class AsNavFor {
	static pluginName = 'as-nav-for';

	/**
	 * @constructor
	 * @param {Object} ctx - the carousel context that will be synced with another
	 */
	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;
		_.otherCarousel = null;
		_.isNavCarousel = true; // Flag to identify this as a navigation carousel
		_.isActive = false; // Only active when asNavFor is configured and found

		// define all event handlers in one object
		_.handlers = {
			// handler for other carousel's movement requested event
			otherMovementRequested: ({ index }) => {
				const currentIndex = _.ctx.store.getState().selectedIndex;
				// return if we are already going to that index or already at it
				if (currentIndex === index) {
					return;
				}
				// go to same selected index as other carousel (always center for nav)
				setTimeout(() => {
					_.ctx.commands.goToSlide(index);
				}, 1);
			},

			// keep selectedIndex in lockstep with renderIndex (only when active)
			renderIndexChanged: ({ currentIndex }) => {
				if (!_.isActive) return;
				const state = _.ctx.store.getState();
				if (state.selectedIndex !== currentIndex) {
					_.ctx.store.setState({ selectedIndex: currentIndex });
				}
			},

			// handler for carousel's optionsChanged event
			optionsChanged: () => {
				_.reInit();
			},

			// handler for carousel's selectedIndexChanged event
			selectedIndexChanged: ({ previousIndex, currentIndex }) => {
				if (!_.isActive) return;
				_.updateOtherCarousel();
				_.updateNavClasses(previousIndex, currentIndex);
			},

			// handler for keyboard navigation
			keyDown: (event) => {
				if (!_.isActive) return;
				_.handleKeyDown(event);
			},

			// handler for slide clicks (nav-specific)
			slideClick: ({ index }) => {
				if (!_.isActive) return;
				// Focus the clicked slide for consistent keyboard capture
				const slides = _.ctx.store.getSlides();
				const targetSlide = slides[index];
				if (targetSlide && typeof targetSlide.focus === 'function') {
					try {
						targetSlide.focus({ preventScroll: true });
					} catch { /* focus may fail */ }
				}
				// When nav slide is clicked, go to that slide and update main carousel
				_.ctx.commands.goToSlide(index);
			},
		};

		_.init();
	}

	/**
	 * query the other carousel from the DOM
	 */
	queryDOM() {
		const _ = this;
		const options = _.ctx.store.getOptions();
		const otherCarouselSelector = options.asNavFor;

		// exit (and disable) if no value
		if (!otherCarouselSelector) {
			_.disableNavUI?.();
			_.isActive = false;
			return false;
		}

		const otherCarousel = document.querySelector(otherCarouselSelector);
		// can't find it -> disable
		if (!otherCarousel) {
			_.disableNavUI?.();
			_.isActive = false;
			return false;
		}

		_.otherCarousel = otherCarousel;
		_.isActive = true;
		return true;
	}

	/**
	 * initialize the sync functionality and nav-specific features
	 */
	init() {
		this.bindEvents();
		this.setupConnection();
	}

	/**
	 * setup the connection to the other carousel
	 * Called from init() and reInit()
	 */
	setupConnection() {
		const _ = this;

		if (!_.queryDOM()) return;

		// Add nav-specific styling + keyboard
		_.enableNavUI?.();

		// give DOM a few ms to load the custom elements
		setTimeout(() => {
			try {
				_.otherCarousel.on(_.ctx.events.movement.requested, _.handlers.otherMovementRequested);
			} catch {
				console.warn(
					'AsNavFor: failed to bind to other carousel - ensure target is a tarot-carousel element'
				);
				_.isActive = false;
				return;
			}
			// Initialize nav classes
			_.updateNavClasses(-1, _.ctx.store.getState().selectedIndex);
			// Ensure selection matches render index at startup
			const { renderIndex, selectedIndex } = _.ctx.store.getState();
			if (renderIndex !== selectedIndex) {
				_.ctx.store.setState({ selectedIndex: renderIndex });
			}
		}, 40);
	}

	/**
	 * reinitialize on carousel option changes
	 */
	reInit() {
		const _ = this;
		// cleanup existing connection
		if (_.otherCarousel) {
			try {
				_.otherCarousel.off(_.ctx.events.movement.requested, _.handlers.otherMovementRequested);
			} catch {
				// silent - carousel may not have been properly connected
			}
			_.otherCarousel = null;
		}
		// disable UI until proven active again
		_.disableNavUI();
		_.isActive = false;
		// reinitialize connection with new options (don't rebind events)
		_.setupConnection();
	}

	/**
	 * bind carousel events to handlers
	 */
	bindEvents() {
		const emitter = this.ctx.emitter;
		const EVENTS = this.ctx.events;
		const handlers = this.handlers;
		// bind to carousel events using new event system
		emitter.on(EVENTS.store.optionsChanged, handlers.optionsChanged);
		emitter.on(EVENTS.store.renderIndexChanged, handlers.renderIndexChanged);
		emitter.on(EVENTS.store.selectedIndexChanged, handlers.selectedIndexChanged);
		emitter.on(EVENTS.slides.click, handlers.slideClick);
	}

	/** enable nav-specific UI and keyboard only when active */
	enableNavUI() {
		const _ = this;
		const ctx = _.ctx;
		const carousel = ctx.carousel;
		if (!_.isActive) return;
		carousel.classList.add('tarot-nav-carousel');
		ctx.track.addEventListener('keydown', _.handlers.keyDown, true);
		carousel.setAttribute('tabindex', '0');
		carousel.setAttribute('role', 'tablist');
		carousel.setAttribute('aria-label', 'Carousel Navigation');
		carousel.removeAttribute('aria-roledescription');

		// Ensure slides have correct roles and initial roving tabindex
		const slides = ctx.store.getSlides();
		const sel = ctx.store.getState().selectedIndex;
		for (let i = 0; i < slides.length; i++) {
			const s = slides[i];
			s.setAttribute('role', 'tab');
			s.removeAttribute('aria-roledescription');
			s.setAttribute('tabindex', i === sel ? '0' : '-1');
		}
	}

	/** disable nav-specific UI and keyboard when not active */
	disableNavUI() {
		const _ = this;
		const ctx = _.ctx;
		const carousel = ctx.carousel;

		carousel.classList.remove('tarot-nav-carousel');
		ctx.track.removeEventListener('keydown', _.handlers.keyDown, true);
		// Don't remove tabindex - core carousel needs it for arrow key support
		carousel.removeAttribute('aria-label');
		carousel.setAttribute('role', 'group');
		carousel.setAttribute('aria-roledescription', 'carousel');

		// Clean up nav-specific ARIA attributes from slides
		const slides = ctx.store.getSlides();
		slides.forEach((slide) => {
			slide.removeAttribute('aria-selected');
			slide.removeAttribute('tabindex');
			slide.setAttribute('role', 'group');
			slide.setAttribute('aria-roledescription', 'slide');
		});
	}

	/**
	 * Handle keyboard navigation for nav carousel
	 */
	handleKeyDown(event) {
		const _ = this;
		const currentIndex = _.ctx.store.getState().selectedIndex;
		const totalSlides = _.ctx.store.getState().slideCount;
		switch (event.key) {
			case 'ArrowLeft':
			case 'ArrowUp': {
				event.preventDefault();
				event.stopPropagation(); // prevent core carousel arrow handling
				const prevIndex =
					currentIndex > 0
						? currentIndex - 1
						: _.ctx.store.getOptions().loop
							? totalSlides - 1
							: currentIndex;
				_.ctx.commands.goToSlide(prevIndex);
				// Move focus to the newly selected tab
				const prevSlides = _.ctx.store.getSlides();
				const prevTarget = prevSlides[prevIndex];
				if (prevTarget && typeof prevTarget.focus === 'function') {
					setTimeout(() => {
						try {
							prevTarget.focus({ preventScroll: true });
						} catch { /* focus may fail */ }
					}, 0);
				}
				break;
			}

			case 'ArrowRight':
			case 'ArrowDown': {
				event.preventDefault();
				event.stopPropagation(); // prevent core carousel arrow handling
				const nextIndex =
					currentIndex < totalSlides - 1
						? currentIndex + 1
						: _.ctx.store.getOptions().loop
							? 0
							: currentIndex;
				_.ctx.commands.goToSlide(nextIndex);
				// Move focus to the newly selected tab
				const nextSlides = _.ctx.store.getSlides();
				const nextTarget = nextSlides[nextIndex];
				if (nextTarget && typeof nextTarget.focus === 'function') {
					setTimeout(() => {
						try {
							nextTarget.focus({ preventScroll: true });
						} catch { /* focus may fail */ }
					}, 0);
				}
				break;
			}

			case 'Home': {
				event.preventDefault();
				event.stopPropagation(); // prevent core carousel arrow handling
				_.ctx.commands.goToSlide(0);
				// Focus first tab
				const slides = _.ctx.store.getSlides();
				if (slides[0]?.focus)
					setTimeout(() => {
						try {
							slides[0].focus({ preventScroll: true });
						} catch { /* focus may fail */ }
					}, 0);
				break;
			}

			case 'End': {
				event.preventDefault();
				event.stopPropagation(); // prevent core carousel arrow handling
				_.ctx.commands.goToSlide(totalSlides - 1);
				// Focus last tab
				const slides2 = _.ctx.store.getSlides();
				const last = totalSlides - 1;
				if (slides2[last]?.focus)
					setTimeout(() => {
						try {
							slides2[last].focus({ preventScroll: true });
						} catch { /* focus may fail */ }
					}, 0);
				break;
			}
		}
	}

	/**
	 * Update navigation-specific classes on slides
	 * Note: The main tarot-selected class is handled by ClassManager,
	 * but we add nav-specific ARIA attributes for accessibility
	 */
	updateNavClasses(previousIndex, currentIndex) {
		const _ = this;
		const slides = _.ctx.store.getSlides();

		// Update ARIA attributes for navigation
		if (previousIndex >= 0 && slides[previousIndex]) {
			slides[previousIndex].setAttribute('aria-selected', 'false');
			// roving tabindex: make previous non-focusable
			slides[previousIndex].setAttribute('tabindex', '-1');
		}

		// Add ARIA attributes to current slide
		if (slides[currentIndex]) {
			slides[currentIndex].setAttribute('aria-selected', 'true');
			slides[currentIndex].setAttribute('role', 'tab');
			// roving tabindex: make current focusable
			slides[currentIndex].setAttribute('tabindex', '0');
		}

		// The tarot-selected class is automatically managed by ClassManager
		// based on slide.selected property, so we don't need to manually manage it here
	}

	/**
	 * update the other carousel to match this one
	 */
	updateOtherCarousel() {
		const _ = this;
		// return if we don't have a carousel to update
		if (!_.otherCarousel) return;

		// get current selected index from store
		const currentIndex = _.ctx.store.getState().selectedIndex;
		// tell carousel to go to the same selected index
		// async hack
		setTimeout(() => {
			try {
				_.otherCarousel.goToSlide(currentIndex);
			} catch (e) {
				// silent - other carousel may have been destroyed
			}
		}, 1);
	}

	/**
	 * destroy the sync, unbinding all events and cleaning up nav functionality
	 */
	destroy() {
		const _ = this;

		const emitter = _.ctx.emitter;
		const EVENTS = _.ctx.events;

		// unbind from carousel events using new event system
		emitter.off(EVENTS.store.optionsChanged, _.handlers.optionsChanged);
		emitter.off(EVENTS.store.renderIndexChanged, _.handlers.renderIndexChanged);
		emitter.off(EVENTS.store.selectedIndexChanged, _.handlers.selectedIndexChanged);
		emitter.off(EVENTS.slides.click, _.handlers.slideClick);

		// Remove nav-specific UI
		_.disableNavUI();

		// unbind from other carousel events
		if (_.otherCarousel) {
			try {
				_.otherCarousel.off(_.ctx.events.movement.requested, _.handlers.otherMovementRequested);
			} catch (e) {
				// silent - carousel may not have been properly connected
			}
		}

		_.otherCarousel = null;
		_.isActive = false;
	}
}
